import type { SxProps, Theme } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useGetDisciplinaryReport } from "../../DisciplinaryReport/useDisciplinaryReport";
import { disciplinaryDataToLineDataGroups } from "../utils";

type Props = {
	dateFrom: DateTime;
	dateTo?: DateTime;
	roles?: string[];
	shiftHours?: string[];
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	shrinkLegendForSmallScreens?: boolean;
	departmentId?: string;
};

export const DisciplinaryOverTime: FC<Props> = ({ dateFrom, dateTo, roles, shiftHours, trailing, containerSx, shrinkLegendForSmallScreens, departmentId }) => {
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const { timezone } = useDepartmentFlags();
	const { data, isLoading } = useGetDisciplinaryReport({ startDate: dateFrom, endDate: dateTo, roles, shiftHours, departmentId });

	const handleClick = () => {
		track("disciplinary_report_opened");
		navigate("/disciplinary-report");
	};

	const transformedData = !isLoading && data ? disciplinaryDataToLineDataGroups(data, timezone) : undefined;

	return (
		<CommonDashboardBox
			onClick={handleClick}
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "start",
				gap: 1,
				width: "calc(33% - 4px)",
				...containerSx,
				cursor: "pointer",
				"&:hover": { boxShadow: "0px 2px 12px 0px #7a5a9e95" },
				transition: "box-shadow 0.2s",
			}}
		>
			<CommonDashboardHeader label={"Disciplinary"} trailing={trailing} />
			<InHouseLineChart
				shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
				width={"100%"}
				height={containerSx ? "100%" : undefined}
				leftAxisData={
					transformedData
						? {
								minValue: 0,
								dataGroups: transformedData,
							}
						: undefined
				}
				margin={{
					top: 4,
					right: 24,
					bottom: 0,
					left: 0,
				}}
			/>
		</CommonDashboardBox>
	);
};
